/**
 * BL-180 Gate 2 — QuickBooks Online OAuth helpers
 *
 * Pure helper functions for the QBO OAuth 2.0 flow.  This module contains NO
 * I/O or SDK calls — it builds URLs and parses token responses so that both
 * Edge Functions and the React UI can import it without pulling in server-only
 * dependencies.
 *
 * Credentials (QBO_CLIENT_ID, QBO_CLIENT_SECRET) are read from environment
 * variables at call time so that this module is safe to import in any context.
 * All callers must check that those variables are set and fail gracefully with a
 * clear error message when they are not — see the Edge Functions for the
 * canonical pattern.
 *
 * QBO OAuth documentation:
 *   https://developer.intuit.com/app/developer/qbo/docs/develop/authentication-and-authorization
 *
 * Token lifetimes:
 *   • Access token:  ~60 minutes
 *   • Refresh token: 100 days of inactivity; reset on each successful refresh
 *
 * Reconnection warning threshold: status is set to 'token_expired' when the
 * refresh token has fewer than REFRESH_TOKEN_WARN_DAYS days remaining so the
 * user has time to reconnect before being locked out.
 */

// ── Constants ─────────────────────────────────────────────────────────────────

export const QBO_AUTH_BASE_URL = 'https://appcenter.intuit.com/connect/oauth2';
export const QBO_TOKEN_URL = 'https://oauth.platform.intuit.com/oauth2/v1/tokens/bearer';
export const QBO_REVOKE_URL = 'https://developer.api.intuit.com/v2/oauth2/tokens/revoke';

/** QBO scopes required for invoice + payment sync. */
export const QBO_SCOPE = 'com.intuit.quickbooks.accounting';

/**
 * Days remaining on the refresh token below which a reconnection prompt is
 * shown to the user (status → 'token_expired').
 */
export const REFRESH_TOKEN_WARN_DAYS = 10;

// ── URL builder ───────────────────────────────────────────────────────────────

/**
 * Builds the QBO OAuth 2.0 authorisation URL that redirects the user to the
 * Intuit consent screen.
 *
 * @param {{
 *   clientId: string,
 *   redirectUri: string,
 *   state: string,
 *   scope?: string,
 * }} params
 * @returns {string}  The full authorisation URL.
 * @throws {Error}    If clientId, redirectUri, or state are missing.
 */
export function buildQboAuthUrl({ clientId, redirectUri, state, scope = QBO_SCOPE }) {
  if (!clientId) throw new Error('QBO_CLIENT_ID is not set — cannot build auth URL.');
  if (!redirectUri) throw new Error('redirectUri is required — cannot build auth URL.');
  if (!state) throw new Error('state is required to prevent CSRF — cannot build auth URL.');

  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope,
    state,
  });

  return `${QBO_AUTH_BASE_URL}?${params.toString()}`;
}

// ── Token exchange ────────────────────────────────────────────────────────────

/**
 * Parses the raw token response from QBO and returns normalised token data.
 *
 * QBO token response shape:
 *   {
 *     access_token: string,
 *     refresh_token: string,
 *     expires_in: number,          // seconds until access token expires
 *     x_refresh_token_expires_in: number,  // seconds until refresh token expires
 *     token_type: 'bearer',
 *   }
 *
 * @param {object} rawResponse  Parsed JSON body from the QBO token endpoint.
 * @param {Date}   [issuedAt]   Reference time; defaults to `new Date()`.
 * @returns {{
 *   accessToken: string,
 *   refreshToken: string,
 *   tokenExpiresAt: string,           // ISO 8601
 *   refreshTokenExpiresAt: string,    // ISO 8601
 * }}
 * @throws {Error}  If the response does not contain expected token fields.
 */
export function parseTokenResponse(rawResponse, issuedAt = new Date()) {
  const { access_token, refresh_token, expires_in, x_refresh_token_expires_in } = rawResponse ?? {};

  if (!access_token) throw new Error('QBO token response missing access_token.');
  if (!refresh_token) throw new Error('QBO token response missing refresh_token.');
  if (typeof expires_in !== 'number') throw new Error('QBO token response missing expires_in.');
  if (typeof x_refresh_token_expires_in !== 'number') {
    throw new Error('QBO token response missing x_refresh_token_expires_in.');
  }

  const tokenExpiresAt = new Date(issuedAt.getTime() + expires_in * 1000).toISOString();
  const refreshTokenExpiresAt = new Date(
    issuedAt.getTime() + x_refresh_token_expires_in * 1000
  ).toISOString();

  return { accessToken: access_token, refreshToken: refresh_token, tokenExpiresAt, refreshTokenExpiresAt };
}

// ── Reconnection prompt logic ─────────────────────────────────────────────────

/**
 * Returns true when a QBO connection's refresh token is expired or will expire
 * within REFRESH_TOKEN_WARN_DAYS days, meaning the user must re-authenticate.
 *
 * @param {{ qbo_refresh_token_expires_at: string | null }} connection
 * @param {Date} [now]  Reference time; defaults to `new Date()`.
 * @returns {boolean}
 */
export function isRefreshTokenExpiring(connection, now = new Date()) {
  const expiresAt = connection?.qbo_refresh_token_expires_at;
  if (!expiresAt) return false;

  const msRemaining = new Date(expiresAt).getTime() - now.getTime();
  const daysRemaining = msRemaining / (1000 * 60 * 60 * 24);
  return daysRemaining <= REFRESH_TOKEN_WARN_DAYS;
}

/**
 * Returns true when a QBO connection's access token has expired (or will expire
 * within the next 60 seconds — a small buffer to avoid using a token that is
 * about to expire mid-request).
 *
 * @param {{ qbo_token_expires_at: string | null }} connection
 * @param {Date} [now]  Reference time; defaults to `new Date()`.
 * @returns {boolean}
 */
export function isAccessTokenExpired(connection, now = new Date()) {
  const expiresAt = connection?.qbo_token_expires_at;
  if (!expiresAt) return true; // treat missing as expired

  const bufferMs = 60 * 1000; // 60-second buffer
  return new Date(expiresAt).getTime() - now.getTime() <= bufferMs;
}

// ── Basic Auth header builder ─────────────────────────────────────────────────

/**
 * Builds the Base64-encoded Basic Auth header value required by the QBO token
 * endpoint (`client_id:client_secret`).
 *
 * Works in both browser (btoa) and Deno/Node (Buffer) environments.
 *
 * @param {string} clientId
 * @param {string} clientSecret
 * @returns {string}  The value to pass as `Authorization: Basic <value>`.
 */
export function buildBasicAuthHeader(clientId, clientSecret) {
  const credentials = `${clientId}:${clientSecret}`;
  // btoa is available in browsers and Deno; Buffer.from is available in Node.
  if (typeof btoa === 'function') {
    return btoa(credentials);
  }
  // Node.js fallback (used in Jest tests).
  return Buffer.from(credentials).toString('base64');
}
